import { computeGrid, overallTotal, isPercentUnit, GRIDLESS_FREQUENCIES } from './targetGrid';
import type { Frequency, TargetAggregation } from '../types';

// Maps Django field names to Arabic display names
const FIELD_NAMES: Record<string, string> = {
  name: 'اسم المؤشر',
  description: 'الوصف',
  kpi_type: 'نوع المؤشر',
  unit: 'وحدة القياس',
  target_value: 'القيمة المستهدفة',
  worst_acceptable_value: 'القيمة القصوى المقبولة',
  frequency: 'دورية القياس',
  polarity: 'اتجاه المؤشر',
  weight: 'الوزن',
  classification: 'التصنيف',
  measurement_formula: 'معادلة القياس',
  measurement_start_date: 'تاريخ بداية القياس',
  data_source: 'مصدر البيانات',
  owner: 'مالك المؤشر',
  source_type: 'نوع المصدر',
  strategic_goal: 'الهدف الاستراتيجي',
  initiative: 'المبادرة',
  source_process: 'العملية',
  source_sub_process: 'العملية الفرعية',
  source_service: 'الخدمة',
  source_activity: 'النشاط',
  organizational_unit_goal: 'هدف الوحدة التنظيمية',
};

/**
 * Extracts a human-readable error message from Django REST Framework 400 responses.
 * DRF returns field-level errors as { field_name: ["error message"] }
 */
export function extractKpiErrorMessage(errorData: any): string {
  if (!errorData) return '';
  if (typeof errorData === 'string') return errorData;
  if (errorData.detail) return errorData.detail;
  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(', ')
      : errorData.non_field_errors;
  }

  // Field-level errors: { field: ["msg1"], field2: ["msg2"] }
  const fieldErrors: string[] = [];
  for (const [field, messages] of Object.entries(errorData)) {
    const label = FIELD_NAMES[field] || field;
    const msgs = Array.isArray(messages) ? messages.join(', ') : String(messages);
    fieldErrors.push(`${label}: ${msgs}`);
  }
  return fieldErrors.join(' | ');
}

/**
 * Required KPI fields config: { formField, label (Arabic) }
 * Used for frontend validation before submitting.
 * "startDate" variant is for screens using startDate, "measurementStartDate" for Process screens.
 */
interface KpiRequiredField {
  field: string;
  label: string;
}

const REQUIRED_FIELDS: KpiRequiredField[] = [
  { field: 'name', label: 'اسم المؤشر' },
  { field: 'description', label: 'وصف المؤشر' },
  { field: 'kpiType', label: 'نوع المؤشر' },
  { field: 'unit', label: 'وحدة القياس' },
  { field: 'targetValue', label: 'القيمة المستهدفة' },
  { field: 'frequency', label: 'دورية القياس' },
  { field: 'polarity', label: 'اتجاه المؤشر' },
  // NOTE: formula (معادلة القياس), unit description, and data source are
  // optional — backend allows blank, so they are intentionally NOT validated.
  // `targetValue` stays a presence-required field; every inline screen seeds it
  // to the '0.0' sentinel (the real target is authored via the annual grid).
];

/**
 * Whether a KPI form row represents an already-saved (existing) KPI rather than
 * a newly-added one. The inline KPI forms each mark "new" differently, so this
 * covers every scheme in use:
 *   - explicit `isExisting` flag (Activity/Service/UnitGoal screens)
 *   - `new_…` prefixed temp ids (Goal screen)
 *   - `Date.now()` temp ids — large integers (Initiative/Activity screens)
 *   - `Math.random()` non-numeric temp ids (Process/SubProcess screens)
 * Real backend ids are small positive integers (< 1,000,000), matching the
 * threshold the Initiative screen itself uses.
 */
function isExistingKpi(kpi: any): boolean {
  if (kpi.isExisting === true) return true;
  if (kpi.isExisting === false) return false;
  const idStr = kpi.id == null ? '' : String(kpi.id);
  if (idStr.startsWith('new_')) return false;
  const n = parseInt(idStr, 10);
  if (isNaN(n) || n <= 0 || n >= 1000000) return false;
  return true;
}

/**
 * Derives the effective target for a grid-authored (new) KPI by mirroring
 * buildKpiTargetPayload: compute the annual grid from the kpi.target config and
 * aggregate it the same way the save payload does. Returns NaN when the grid
 * cannot be computed yet (missing frequency / start date / config) so callers
 * skip the >0 check until those required fields are filled.
 */
function gridTargetValue(kpi: any, dateField: string): number {
  const cfg = kpi.target;
  const startDate = kpi[dateField] || '';
  if (!cfg || !kpi.frequency || !startDate) return NaN;

  const cells = computeGrid({
    frequency: kpi.frequency as Frequency,
    pattern: cfg.targetPattern,
    unit: kpi.unit,
    start: startDate || null,
    end: cfg.verificationEndDate || null,
    stepPercent: parseFloat(cfg.targetStepPercent) || 0,
    stepAmount: parseFloat(cfg.targetStepAmount) || 0,
    inputs: cfg.inputs || {},
  });
  if (cells.length === 0) return NaN;

  const aggregation: TargetAggregation = isPercentUnit(kpi.unit) ? 'last' : cfg.targetAggregation;
  return overallTotal(cells.map((c) => c.value), kpi.unit, aggregation);
}

/**
 * Effective numeric target for a KPI form row — the single source of truth a
 * screen should validate against. Mirrors the save payload: the computed annual
 * grid total when the grid is authorable (start + end + frequency), else the
 * scalar `targetValue` (gridless frequencies, or before the grid dates are set).
 *
 * Screens MUST use this instead of `parseFloat(kpi.targetValue)`: the target is
 * authored through the grid, so the scalar is a stale '0' sentinel and reading
 * it directly produces a false "القيمة المستهدفة" error even when the grid is
 * filled. Returns NaN only when neither source is parseable.
 */
export function resolveKpiTarget(kpi: any, dateField: string = 'startDate'): number {
  const gridTarget = gridTargetValue(kpi, dateField);
  return !isNaN(gridTarget) ? gridTarget : parseFloat(kpi.targetValue);
}

/**
 * Validates required fields on all KPIs before save.
 * Returns null if valid, or a combined error message string listing
 * all missing fields across all KPIs (one line per KPI with issues).
 * dateField: the field name for measurement start date (varies per screen).
 */
export function validateKpiRequiredFields(
  kpis: any[],
  t: (key: string, opts?: any) => string,
  dateField: string = 'startDate',
): string | null {
  const allErrors: string[] = [];

  for (const kpi of kpis) {
    const name = kpi.name || kpi[REQUIRED_FIELDS[0].field];
    if (!name || !name.trim()) continue; // skip empty KPIs

    const missingFields: string[] = [];

    for (const { field, label } of REQUIRED_FIELDS) {
      const val = kpi[field];
      if (!val || (typeof val === 'string' && !val.trim())) {
        missingFields.push(label);
      }
    }

    // Target value checks. Every screen using this validator authors the target
    // through the annual grid (no editable scalar `targetValue` field is rendered)
    // for BOTH new and existing KPIs, so the effective target is the grid value
    // (same math as buildKpiTargetPayload — which the backend then mirrors onto
    // KPI.target_value). The stored scalar is stale here: an existing KPI saved
    // with an empty/zero grid loads targetValue '0', so reading it would reject a
    // freshly grid-entered target with "must be > 0" and block the save. Fall back
    // to the scalar only when the grid can't be computed (gridless frequency /
    // missing dates), where the scalar still drives the save.
    const isNegative = kpi.polarity === 'negative';
    const isPercent = kpi.unit === '%';
    const isExisting = isExistingKpi(kpi);

    const freq = kpi.frequency;
    const usesGrid = !!freq && !GRIDLESS_FREQUENCIES.has(freq as Frequency);
    const hasEnd = !!(kpi.target && kpi.target.verificationEndDate);
    const hasStart = !!(kpi[dateField] && String(kpi[dateField]).trim());

    const gridTarget = gridTargetValue(kpi, dateField);
    const targetNum = !isNaN(gridTarget) ? gridTarget : parseFloat(kpi.targetValue);

    if (usesGrid && isNaN(gridTarget) && hasStart && !hasEnd) {
      // The annual grid needs a start+end window to render any target cell; with
      // a start but no end date it silently yields a zero target. Surface the
      // real, fixable cause (the missing verification end date) instead of a
      // phantom "القيمة المستهدفة يجب أن تكون أكبر من 0" pointing at a field the
      // user can't see. Every OTHER uncomputable-grid case (missing start, or an
      // end-before-start that yields no periods) falls through to the target
      // check below, which keeps blocking the save rather than persisting a 0.
      missingFields.push('تاريخ التحقق (نهاية القياس) — مطلوب لإدخال القيم المستهدفة');
    } else if (!isNaN(targetNum)) {
      // Grid value (when computable) or the scalar for gridless frequencies.
      if (!isNegative && targetNum <= 0) {
        missingFields.push('القيمة المستهدفة (يجب أن تكون أكبر من 0)');
      } else if (isNegative && targetNum < 0) {
        missingFields.push('القيمة المستهدفة (يجب أن تكون 0 أو أكبر)');
      }
    }

    // Worst-acceptable value checks apply to the scalar-authored (existing) flow
    // only — the field isn't rendered for new KPIs (they use the grid).
    if (isExisting && isNegative && !isPercent) {
      const worstStr = kpi.worstAcceptableValue;
      const worstNum = worstStr === '' || worstStr == null ? null : parseFloat(worstStr);

      if (targetNum === 0 && worstNum === null) {
        missingFields.push('القيمة القصوى المقبولة');
      } else if (worstNum !== null && !isNaN(worstNum) && !isNaN(targetNum) && worstNum <= targetNum) {
        missingFields.push('القيمة القصوى المقبولة (يجب أن تكون أكبر من القيمة المستهدفة)');
      }
    }

    // Check measurement start date
    const dateVal = kpi[dateField];
    if (!dateVal || (typeof dateVal === 'string' && !dateVal.trim())) {
      missingFields.push('تاريخ بداية القياس');
    }

    if (missingFields.length > 0) {
      allErrors.push(`${name}: ${missingFields.join('، ')}`);
    }
  }

  if (allErrors.length === 0) return null;
  return allErrors.join('\n');
}

/**
 * Validates that KPI measurement_start_date does not exceed the strategic goal's target_completion_date.
 * Returns null if valid, or an error message string.
 */
export function validateKpiMeasurementDate(
  kpis: any[],
  goalTargetDate: string,
  dateField: string = 'startDate',
): string | null {
  if (!goalTargetDate) return null;

  const errors: string[] = [];
  for (const kpi of kpis) {
    const name = kpi.name;
    if (!name || !name.trim()) continue;

    const dateVal = kpi[dateField];
    if (dateVal && dateVal > goalTargetDate) {
      errors.push(
        `${name}: تاريخ بداية القياس (${dateVal}) يجب ألا يتجاوز تاريخ الإنجاز المستهدف للهدف (${goalTargetDate})`
      );
    }
  }

  if (errors.length === 0) return null;
  return errors.join('\n');
}
